import type { FormDefinition, SelectOption } from './types';

// Admin-UI form definition for the captomi-office app. Per @captomi/app-config SPEC §7.1,
// labels / descriptions / destructive flags live with the admin tool — separate from the
// runtime Zod schema in captomi-office/src/config/app-config.ts. Keys MUST match the
// CaptomiOfficeConfigSchema in that file; drift is caught by Zod's default .strip() on
// the consumer side and the raw-JSON-viewer fallback in the admin UI (SPEC §6 four-state
// table). Each Captomi app drops its own file here as part of its adoption PR.

// One unified "Model" dropdown per role (receptionist, onboarding). Each option is a full
// vendor+model+version choice that writes BOTH underlying config keys — `*_llm_vendor`
// ('gemini' | 'openai') and `*_openai_model` (the OpenAI model id, '' = use OPENAI_MODEL env).
// There's no Gemini-model config key in captomi-office, so Gemini is a single option (the
// model is fixed server-side). Latency figures from the 2026-05-30 painter-prompt
// measurement (handoff PLATFORM_HANDOFF_2026-05-30.md Prompt B).
export function build_model_options(vendor_key: string, model_key: string): SelectOption[] {
  return [
    {
      id: 'gemini-3.5-flash',
      label: 'Gemini 3.5 Flash (current default)',
      description: 'Vendor Gemini · ~1149 ms p50 / 1505 ms p95 TTFT. The platform default.',
      set: { [vendor_key]: 'gemini', [model_key]: '' },
    },
    {
      id: 'gpt-4.1',
      label: 'OpenAI GPT-4.1',
      description: 'Vendor OpenAI · ~563 ms p50 / 810 ms p95 TTFT. Best attention on complex prompts.',
      set: { [vendor_key]: 'openai', [model_key]: 'gpt-4.1' },
    },
    {
      id: 'gpt-4.1-mini',
      label: 'OpenAI GPT-4.1 mini',
      description:
        'Vendor OpenAI · ~352 ms p50 / 474 ms p95 TTFT. Snappier; can be less rigorous on complex prompts.',
      set: { [vendor_key]: 'openai', [model_key]: 'gpt-4.1-mini' },
    },
    {
      id: 'gpt-4.1-nano',
      label: 'OpenAI GPT-4.1 nano',
      description: 'Vendor OpenAI · latency unmeasured — A/B test before relying on it.',
      set: { [vendor_key]: 'openai', [model_key]: 'gpt-4.1-nano' },
    },
  ];
}

const MODEL_TOOLTIP =
  'Vendor + model for the live conversation. Lower latency = snappier voice; lower quality ' +
  'may misapply rules — test before relying on a flip. Takes effect within ~1s, no redeploy.';

export const CaptomiOfficeForm: FormDefinition = {
  app_code: 'captomi_office',
  toggles: [
    {
      type: 'select',
      id: 'receptionist_model',
      label: 'Receptionist model (Peter)',
      description: MODEL_TOOLTIP,
      keys: ['receptionist_llm_vendor', 'receptionist_openai_model'],
      options: build_model_options('receptionist_llm_vendor', 'receptionist_openai_model'),
      default_id: 'gemini-3.5-flash',
    },
    {
      type: 'select',
      id: 'onboarding_model',
      label: 'Onboarding model (Pete)',
      description: MODEL_TOOLTIP,
      keys: ['onboarding_llm_vendor', 'onboarding_openai_model'],
      options: build_model_options('onboarding_llm_vendor', 'onboarding_openai_model'),
      default_id: 'gemini-3.5-flash',
    },
    {
      key: 'fetch_recordings_enabled',
      type: 'boolean',
      label: 'Fetch recordings (scheduled job)',
      description:
        "Master switch for the scheduled job that downloads call recordings from Retell and uploads them to GCS. When OFF, the recording-fetch endpoint short-circuits — pending conversations stay queued but no GCS spend. Safe to leave OFF during dev/test so we don't accumulate test-call recordings.",
      default: false,
    },
    {
      key: 'receptionist_number_provisioning_enabled',
      type: 'boolean',
      label: 'Peter number provisioning',
      description:
        "Master switch for buying a Twilio Peter number when a tenant completes onboarding. When OFF, onboarding still completes (profile_complete flips, trial starts) but no Twilio number is purchased — the close-out degrades to 'you'll get a text'. Turn ON when ready for real pilots.",
      default: false,
      destructive: {
        when: 'enabled',
        confirm_message:
          'Enabling this will cause the NEXT completed Pete onboarding to buy a real Twilio number ($1/mo + per-message fees). Confirm only if pilot onboarding is intended.',
      },
    },
    {
      key: 'receptionist_number_auto_activate',
      type: 'boolean',
      label: 'Auto-activate onboarded receptionist lines',
      description:
        "Does a tenant's line go LIVE without a human after they finish onboarding with Pete? The number is bought, SIP-trunked, A2P-registered, bound to the Retell agent and named either way — this only decides whether it starts answering real calls, or holds until you click 'Activate' below. OFF by default: Pete captures that profile unattended, so a garbage business name or wrong service areas would otherwise start answering real homeowners with nobody having looked. Turn ON for full self-serve. Does not affect the Provision button or the batch scripts — those always activate, because you clicking them IS the review.",
      default: false,
      destructive: {
        when: 'enabled',
        confirm_message:
          "Enabling this means the NEXT completed Pete onboarding goes LIVE with no human review — its receptionist starts answering real customer calls using whatever profile Pete captured. Confirm only if you're happy for onboarding to be fully self-serve.",
      },
    },
    {
      key: 'email_sending_enabled',
      type: 'boolean',
      label: 'Outbound email (SendGrid)',
      description:
        "Master switch for outbound email — demo .ics invites, callback reminders, and lead-forward emails sent after a Peter (receptionist) call. When OFF, the mailer is a no-op and the call still books in Firestore, just silently. Requires SENDGRID_API_KEY on the Cloud Run revision; without that, the toggle has no effect.",
      default: false,
      destructive: {
        when: 'enabled',
        confirm_message:
          'Enabling this will cause the NEXT booked Peter call to send real email via SendGrid to the SDR and (for demo bookings) the prospect. Confirm only if SENDGRID_API_KEY is wired and you want real sends.',
      },
    },
    {
      key: 'sms_sending_enabled',
      type: 'boolean',
      label: 'Outbound SMS (Twilio)',
      description:
        "Master switch for outbound SMS — the booking-confirmation text sent to the SDR's phone after a Peter (receptionist) call. When OFF, the SMS sender is a no-op and the call still books in Firestore, just silently. Requires Twilio sender credentials on the Cloud Run revision.",
      default: false,
      destructive: {
        when: 'enabled',
        confirm_message:
          "Enabling this will cause the NEXT booked Peter call to send a real SMS to the SDR's number via Twilio. Confirm only if Twilio sender credentials are wired and you want real sends.",
      },
    },
    {
      key: 'sms_messaging_service_sid',
      type: 'string',
      label: 'SMS Messaging Service SID (A2P)',
      description:
        'Twilio Messaging Service SID (MG…) that all outbound A2P SMS routes through — pools every receptionist number under the registered 10DLC campaign + Advanced Opt-Out. Paste the MG… once the campaign is approved and the Messaging Service is configured; takes effect within ~1s, no redeploy. Leave blank to fall back to the SMS_FROM_NUMBER env (legacy single number).',
      default: '',
      destructive: {
        when: 'changed',
        confirm_message:
          'Changing the Messaging Service SID re-routes ALL outbound SMS through a different Twilio sender. Confirm only if this MG… is the correct, campaign-linked Messaging Service.',
      },
    },
    {
      key: 'address_validation_enabled',
      type: 'boolean',
      label: 'Address validation (Google Maps)',
      description:
        "Post-call address validation — when a painter Peter call books an on-site estimate or callback, the captured property address is run through Google's Address Validation API. Confirmed/corrected addresses replace the raw LLM capture in the booking + ICS + emails + SMS; ambiguous or not-found results add a confirm-with-homeowner warning to the painter notifications. Fail-open: never blocks a booking. Requires GOOGLE_MAPS_API_KEY on the Cloud Run revision; without that the toggle has no effect. ~$0.017 per lookup.",
      default: false,
      destructive: {
        when: 'enabled',
        confirm_message:
          'Enabling this will cause the NEXT booked Peter painter call to spend ~$0.017 against the google-maps-api-key secret per call. Confirm only if the key is wired and you want live validation on real bookings.',
      },
    },
    {
      key: 'google_calendar_enabled',
      type: 'boolean',
      label: 'Google Calendar conflict avoidance',
      description:
        "Master kill-switch for the Google Calendar integration (office google-calendar-spec). When ON, a tenant who has connected their Google Calendar gets slots offered and re-validated against their real free/busy, so the receptionist never books over a calendar conflict. When OFF, connected tenants are NOT silently dropped back to home-grown scheduling (that would re-expose calendar clashes); instead their bookings route to the tentative path (held as PENDING for the owner to confirm). Does NOT delete or disconnect any tenant's calendar. Unconnected tenants are unaffected either way.",
      default: false,
      destructive: {
        when: 'changed',
        confirm_message:
          "Changing this flips how EVERY connected tenant's calendar conflicts are handled. Turning OFF routes their live bookings to the tentative/owner-confirm path; turning ON resumes live free/busy conflict avoidance. Confirm only if you intend a global change to the calendar integration.",
      },
    },
  ],
};
